/**
 * When a cart line with `_kitwear_bulk_group` / `kitwear_bulk_group` is removed (qty → 0), remove every line
 * with the same group so unrelated grouped fees are not left behind.
 * Handles link clicks, quantity inputs, and theme `fetch` POSTs to `/cart/change.js` (LineItemQuantity).
 * Cart maintenance also reconciles the single cart-global new-artwork setup line from unique SHA-256 IDs.
 */
(function () {
  if (typeof window.themeVariables === 'undefined') {
    return;
  }

  /** Theme `LineItemQuantity` uses `fetch` to POST `/cart/change.js` — bypasses document click handlers; use native fetch for internal calls to avoid recursion when we wrap `window.fetch`. */
  const nativeFetch = window.fetch.bind(window);

  const BULK_GROUP_KEY = '_kitwear_bulk_group';

  /**
   * @param {unknown} raw
   * @returns {Record<string, string>}
   */
  function normalizeProperties(raw) {
    if (!raw || typeof raw !== 'object') {
      return {};
    }
    if (Array.isArray(raw)) {
      /** @type {Record<string, string>} */
      const out = {};
      for (const item of raw) {
        if (item && typeof item === 'object' && 'name' in item) {
          const row = /** @type {{ name?: string; value?: unknown }} */ (item);
          const name = row.name != null ? String(row.name) : '';
          out[name] = row.value != null ? String(row.value) : '';
        }
      }
      return out;
    }
    /** @type {Record<string, string>} */
    const out = {};
    for (const k of Object.keys(/** @type {Record<string, unknown>} */ (raw))) {
      const v = /** @type {Record<string, unknown>} */ (raw)[k];
      out[k] = v != null ? String(v) : '';
    }
    return out;
  }

  const PUBLIC_GROUP_KEY = 'kitwear_bulk_group';
  const FEE_TYPE_KEY = '_kitwear_fee_type';
  const CLUB_BADGE_FEE_TYPE = 'club_badge';
  const FEE_BUCKET_KEY = '_kitwear_fee_bucket';
  const APP_POSITION_KEY = '_kitwear_app_position_key';
  const DEFAULT_APPLICATION_KEY = '_kitwear_default_application';
  const TRAINING_WEAR_APPLICATION_KEY = '_kitwear_training_wear_application';
  const LOGO_POSITION_KEY = 'Logo position';
  const APPLICATION_KEY = 'Application';
  const LOGO_SETUP_TYPE_KEY = 'Logo setup type';
  const LOGO_APPLICATION_KEY = 'Logo application';
  const ARTWORK_ID_KEY = '_logo_artwork_id';
  const ARTWORK_FILE_URL_KEY = '_logo_file_url';
  const ARTWORK_FILENAME_KEY = '_logo_original_filename';
  const ARTWORK_UPLOAD_PROP_KEY = '_logo_upload_property_key';
  const ARTWORK_UPLOAD_PROP_VALUE = '_logo_upload_property_value';
  const ARTWORK_SETUP_VARIANT_KEY = '_logo_setup_variant_id';
  const CENTRAL_LOGO_SETUP_KEY = '_kitwear_logo_setup_central';
  const CENTRAL_LOGO_SETUP_VALUE = 'new sponsor logo setup';
  const MANIFEST_PROPERTY_MAX_LEN = 250;

  /** @type {Set<string>} */
  const LARGE_POSITION_LABELS = new Set([
    'large chest',
    'large centre chest',
    'large center chest',
    'large back',
    'big back',
  ]);

  let bulkQtySyncInProgress = false;
  /** Suppress orphan/qty maintenance briefly after bulk group removal (avoids drawer flash + fetch loop). */
  let maintenanceSuppressedUntil = 0;
  /** @type {number | undefined} */
  let cartMaintenanceTimer;
  let cartMaintenanceRunning = false;

  function suppressCartMaintenance(ms = 900) {
    maintenanceSuppressedUntil = Date.now() + ms;
  }

  function isCartMaintenanceSuppressed() {
    return Date.now() < maintenanceSuppressedUntil || bulkQtySyncInProgress;
  }

  /**
   * Garment quantity only (excludes Kitwear setup / application fee lines).
   * @param {{ items?: Array<{ quantity?: number; properties?: unknown }> }} cart
   * @returns {number}
   */
  function kitwearGarmentCartCountFromCart(cart) {
    const items = cart && Array.isArray(cart.items) ? cart.items : [];
    let sum = 0;
    for (const it of items) {
      const p = normalizeProperties(it.properties || {});
      if (!isKitwearFeeLineProps(p)) {
        sum += it.quantity || 0;
      }
    }
    return sum;
  }

  /**
   * Theme `cart-count` uses Shopify `item_count` (includes fee lines); override after theme handlers run.
   * @param {{ items?: Array<{ quantity?: number; properties?: unknown }> }} cart
   */
  function updateKitwearCartCountDisplays(cart) {
    const count = kitwearGarmentCartCountFromCart(cart);
    const text = String(count);
    document.querySelectorAll('cart-count').forEach((el) => {
      el.textContent = text;
    });
    if (window.themeVariables && window.themeVariables.settings) {
      window.themeVariables.settings.cartCount = count;
    }
  }

  /**
   * @param {unknown} cart
   * @param {number} [replacementDelay]
   */
  function dispatchCartUiRefresh(cart, replacementDelay = 600) {
    if (window.themeVariables.settings.pageType === 'cart') {
      window.location.reload();
      return;
    }
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { cart },
      })
    );
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { cart, replacementDelay },
      })
    );
    queueMicrotask(() => updateKitwearCartCountDisplays(cart));
  }

  /**
   * `/cart.js` items often omit `line`; Shopify change API uses 1-based index in `cart.items`.
   * @param {{ line?: number }} item
   * @param {number} arrayIndex
   */
  function cartLineNumber(item, arrayIndex) {
    if (item && item.line != null && String(item.line) !== '') {
      const n = Number(item.line);
      if (!Number.isNaN(n) && n > 0) {
        return n;
      }
    }
    return arrayIndex + 1;
  }

  /**
   * @param {Array<{ line?: number; properties?: unknown }>} items
   * @param {string} lineStr
   */
  function findCartItemByLine(items, lineStr) {
    const lineNum = parseInt(String(lineStr), 10);
    if (Number.isNaN(lineNum) || lineNum < 1) {
      return null;
    }
    let found = null;
    items.forEach((it, idx) => {
      if (cartLineNumber(it, idx) === lineNum) {
        found = it;
      }
    });
    if (found) {
      return found;
    }
    return items[lineNum - 1] || null;
  }

  /**
   * @param {Record<string, string>} props
   */
  function feeTypeFromProps(props) {
    return String(props[FEE_TYPE_KEY] || '')
      .trim()
      .toLowerCase();
  }

  /**
   * Setup or application fee line (not a garment).
   * @param {Record<string, string>} props
   */
  function isKitwearFeeLineProps(props) {
    const t = feeTypeFromProps(props);
    if (t === 'setup' || t === 'application' || t === CLUB_BADGE_FEE_TYPE) {
      return true;
    }
    if (!props || !props.Setup) {
      return false;
    }
    const s = String(props.Setup).toLowerCase();
    return s.includes('setup') || s.includes('application');
  }

  /**
   * @param {Record<string, string>} props
   */
  function isSetupFeeLineProps(props) {
    const t = feeTypeFromProps(props);
    if (t === 'setup') {
      return true;
    }
    if (!props || !props.Setup) {
      return false;
    }
    return String(props.Setup).toLowerCase().includes('setup');
  }

  /**
   * @param {string} summary
   * @returns {string[]}
   */
  function parseLogoPositionLabelList(summary) {
    return String(summary || '')
      .split(';')
      .map((s) => s.trim())
      .filter(Boolean);
  }

  /**
   * @param {string} label
   */
  function isLargePositionLabel(label) {
    const l = String(label).trim().toLowerCase();
    return (
      LARGE_POSITION_LABELS.has(l) ||
      l.includes('large chest') ||
      l.includes('large back') ||
      l.includes('big back')
    );
  }

  /**
   * @param {Array<{ properties?: unknown }>} items
   * @param {string} groupId
   * @returns {string}
   */
  function groupLogoPositionSummary(items, groupId) {
    for (const it of items) {
      const p = normalizeProperties(it.properties || {});
      if (getLineItemBulkGroup(it) !== groupId || isKitwearFeeLineProps(p)) {
        continue;
      }
      const pos = String(p[LOGO_POSITION_KEY] || '').trim();
      if (pos) {
        return pos;
      }
    }
    for (const it of items) {
      const p = normalizeProperties(it.properties || {});
      if (getLineItemBulkGroup(it) !== groupId || !isKitwearFeeLineProps(p)) {
        continue;
      }
      const pos = String(p[LOGO_POSITION_KEY] || '').trim();
      if (pos) {
        return pos;
      }
    }
    return '';
  }

  /**
   * @param {Array<{ properties?: unknown }>} items
   * @param {string} groupId
   */
  function isEmbroideryGroup(items, groupId) {
    for (const it of items) {
      const p = normalizeProperties(it.properties || {});
      if (getLineItemBulkGroup(it) !== groupId || isKitwearFeeLineProps(p)) {
        continue;
      }
      const t = String(
        p[LOGO_APPLICATION_KEY] || p[LOGO_SETUP_TYPE_KEY] || p['Logo setup'] || ''
      ).toLowerCase();
      if (t.includes('embroider')) {
        return true;
      }
      if (t.includes('print')) {
        return false;
      }
    }
    for (const it of items) {
      const p = normalizeProperties(it.properties || {});
      if (getLineItemBulkGroup(it) !== groupId || !isSetupFeeLineProps(p)) {
        continue;
      }
      return String(p.Setup || '').toLowerCase().includes('embroidery');
    }
    return false;
  }

  /**
   * @param {Array<{ properties?: unknown }>} items
   * @param {string} groupId
   * @returns {'default' | 'training_wear' | ''}
   */
  function groupApplicationMode(items, groupId) {
    let defaultApplication = false;
    for (const it of items) {
      const p = normalizeProperties(it.properties || {});
      if (getLineItemBulkGroup(it) !== groupId) {
        continue;
      }
      if (isKitwearFeeLineProps(p)) {
        continue;
      }
      if (String(p[TRAINING_WEAR_APPLICATION_KEY] || '').trim().toLowerCase() === 'true') {
        return 'training_wear';
      }
      if (String(p[DEFAULT_APPLICATION_KEY] || '').trim().toLowerCase() === 'true') {
        defaultApplication = true;
      }
    }
    return defaultApplication ? 'default' : '';
  }

  /**
   * Charge reconciliation runs only for groups carrying an explicit Default Product or
   * Training Wear marker. Sponsor/new-logo properties on club groups remain ignored.
   * @param {Array<{ properties?: unknown }>} items
   * @param {string} groupId
   */
  function groupUsesLogoApplication(items, groupId) {
    return Boolean(groupApplicationMode(items, groupId));
  }

  /**
   * @param {string} positionSummary
   * @param {boolean} embroidery
   * @returns {Array<{ bucket: 'printLarge' | 'printSmall' | 'embroideryLarge' | 'embroiderySmall'; positionKey: string; label: string }>}
   */
  function expectedApplicationEntries(positionSummary, embroidery) {
    const allLabels = parseLogoPositionLabelList(positionSummary);
    return allLabels.map((label) => {
      const large = isLargePositionLabel(label);
      const bucket = embroidery
        ? large
          ? 'embroideryLarge'
          : 'embroiderySmall'
        : large
          ? 'printLarge'
          : 'printSmall';
      const positionKey = String(label)
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
      return { bucket, positionKey, label };
    });
  }

  /**
   * Training Wear uses only four exact print positions and two fixed price buckets.
   * @param {string} positionSummary
   * @returns {Array<{ bucket: 'trainingPrintLarge' | 'trainingPrintSmall'; positionKey: string; label: string }>}
   */
  function expectedTrainingWearApplicationEntries(positionSummary) {
    const allowed = new Map([
      ['large centre chest', { bucket: 'trainingPrintLarge', positionKey: 'large_chest' }],
      ['large center chest', { bucket: 'trainingPrintLarge', positionKey: 'large_chest' }],
      ['large back', { bucket: 'trainingPrintLarge', positionKey: 'large_back' }],
      ['left sleeve', { bucket: 'trainingPrintSmall', positionKey: 'left_sleeve' }],
      ['right sleeve', { bucket: 'trainingPrintSmall', positionKey: 'right_sleeve' }],
    ]);
    return parseLogoPositionLabelList(positionSummary).flatMap((label) => {
      const mapping = allowed.get(String(label).trim().toLowerCase());
      return mapping ? [{ ...mapping, label }] : [];
    });
  }

  /**
   * Count actual garment quantities carrying a populated per-garment property.
   * @param {Array<{ quantity?: number; properties?: unknown }>} items
   * @param {string} groupId
   * @param {string} propertyKey
   */
  function populatedPropertyQuantityInBulkGroup(items, groupId, propertyKey) {
    let quantity = 0;
    items.forEach((item) => {
      const props = normalizeProperties(item.properties || {});
      if (getLineItemBulkGroup(item) !== groupId || isKitwearFeeLineProps(props)) {
        return;
      }
      if (String(props[propertyKey] || '').trim()) {
        quantity += item.quantity || 0;
      }
    });
    return quantity;
  }

  /**
   * @param {Array<{ quantity?: number; properties?: unknown }>} items
   * @param {string} groupId
   * @returns {Array<{ bucket: 'trainingInitials' | 'trainingSmallNumber'; positionKey: string; label: string; quantity: number }>}
   */
  function expectedTrainingWearPersonalisationEntries(items, groupId) {
    const initialsQty = populatedPropertyQuantityInBulkGroup(items, groupId, 'Initials');
    const smallNumberQty = populatedPropertyQuantityInBulkGroup(items, groupId, 'Small Number');
    const entries = [];
    if (initialsQty > 0) {
      entries.push({
        bucket: 'trainingInitials',
        positionKey: 'initials',
        label: 'Initials',
        quantity: initialsQty,
      });
    }
    if (smallNumberQty > 0) {
      entries.push({
        bucket: 'trainingSmallNumber',
        positionKey: 'small_number',
        label: 'Small Number',
        quantity: smallNumberQty,
      });
    }
    return entries;
  }

  /**
   * @param {Array<{ properties?: unknown }>} items
   * @param {string} groupId
   * @returns {string}
   */
  function applicationAppliesToFromGroup(items, groupId) {
    for (const it of items) {
      const p = normalizeProperties(it.properties || {});
      if (getLineItemBulkGroup(it) !== groupId || isKitwearFeeLineProps(p)) {
        continue;
      }
      return String(it.product_title || it.title || 'Garment').trim() || 'Garment';
    }
    return 'Garment';
  }

  /**
   * Application fee lines expose only their customer-facing application label and Applies to. All grouping,
   * bucket and reconciliation identity remains in private properties.
   * @param {string} groupId
   * @param {string} appliesTo
   * @param {{ bucket: string; positionKey: string; label: string }} expected
   * @param {'default' | 'training_wear'} mode
   */
  function buildApplicationFeeProperties(groupId, appliesTo, expected, mode) {
    const isPersonalisation =
      expected.bucket === 'trainingInitials' || expected.bucket === 'trainingSmallNumber';
    const properties = {
      [isPersonalisation ? APPLICATION_KEY : LOGO_POSITION_KEY]: expected.label,
      'Applies to': appliesTo,
      [BULK_GROUP_KEY]: groupId,
      [FEE_TYPE_KEY]: 'application',
      [FEE_BUCKET_KEY]: expected.bucket,
      [APP_POSITION_KEY]: expected.positionKey,
    };
    properties[mode === 'training_wear' ? TRAINING_WEAR_APPLICATION_KEY : DEFAULT_APPLICATION_KEY] =
      'true';
    return properties;
  }

  /**
   * @param {Array<{ line?: number; quantity?: number; properties?: unknown }>} items
   * @param {string} groupId
   * @returns {number}
   */
  function garmentQuantityInBulkGroup(items, groupId) {
    let sum = 0;
    items.forEach((it) => {
      if (getLineItemBulkGroup(it) !== groupId) {
        return;
      }
      const p = normalizeProperties(it.properties || {});
      if (isKitwearFeeLineProps(p)) {
        return;
      }
      sum += it.quantity || 0;
    });
    return sum;
  }

  /**
   * @param {number} applicationCount
   * @returns {number}
   */
  function applicationTierIndex(applicationCount) {
    const n = applicationCount || 0;
    if (n <= 0) {
      return -1;
    }
    if (n <= 9) {
      return 0;
    }
    if (n <= 24) {
      return 1;
    }
    if (n <= 49) {
      return 2;
    }
    if (n <= 74) {
      return 3;
    }
    if (n <= 99) {
      return 4;
    }
    if (n <= 249) {
      return 5;
    }
    return 6;
  }

  /**
   * @returns {{ printLarge: number[]; printSmall: number[]; embroideryLarge: number[]; embroiderySmall: number[] } | null}
   */
  function readAppTierConfig() {
    const el = document.querySelector('[data-kitwear-app-tier-config]');
    if (!el || !el.textContent) {
      return null;
    }
    try {
      const raw = JSON.parse(el.textContent.trim());
      if (!raw || typeof raw !== 'object') {
        return null;
      }
      const pick = (key) =>
        Array.isArray(raw[key])
          ? raw[key].map((id) => parseInt(String(id), 10)).filter((id) => !Number.isNaN(id) && id > 0)
          : [];
      return {
        printLarge: pick('printLarge'),
        printSmall: pick('printSmall'),
        embroideryLarge: pick('embroideryLarge'),
        embroiderySmall: pick('embroiderySmall'),
      };
    } catch {
      return null;
    }
  }

  /**
   * @returns {{ printLarge: number; printSmall: number; personalisation: number; personalisationAvailable: boolean } | null}
   */
  function readTrainingWearAppConfig() {
    const el = document.querySelector('[data-kitwear-training-wear-app-config]');
    if (!el || !el.textContent) {
      return null;
    }
    try {
      const raw = JSON.parse(el.textContent.trim());
      const printLarge = parseInt(String(raw && raw.printLarge), 10);
      const printSmall = parseInt(String(raw && raw.printSmall), 10);
      const personalisation = parseInt(String(raw && raw.personalisation), 10);
      return {
        printLarge: printLarge > 0 ? printLarge : 0,
        printSmall: printSmall > 0 ? printSmall : 0,
        personalisation: personalisation > 0 ? personalisation : 0,
        personalisationAvailable: Boolean(raw && raw.personalisationAvailable === true),
      };
    } catch {
      return null;
    }
  }

  /**
   * @param {Record<string, string>} props
   * @returns {'printLarge' | 'printSmall' | 'embroideryLarge' | 'embroiderySmall' | 'trainingPrintLarge' | 'trainingPrintSmall' | 'trainingInitials' | 'trainingSmallNumber' | ''}
   */
  function feeBucketFromProps(props) {
    const explicit = String(props[FEE_BUCKET_KEY] || '').trim();
    if (
      explicit === 'printLarge' ||
      explicit === 'printSmall' ||
      explicit === 'embroideryLarge' ||
      explicit === 'embroiderySmall' ||
      explicit === 'trainingPrintLarge' ||
      explicit === 'trainingPrintSmall' ||
      explicit === 'trainingInitials' ||
      explicit === 'trainingSmallNumber'
    ) {
      return explicit;
    }
    const setup = String(props.Setup || '').toLowerCase();
    const large = isLargePositionLabel(String(props[LOGO_POSITION_KEY] || ''));
    if (setup.includes('embroidery')) {
      return large ? 'embroideryLarge' : 'embroiderySmall';
    }
    if (setup.includes('large back')) {
      return 'printLarge';
    }
    if (setup.includes('print')) {
      return 'printSmall';
    }
    return '';
  }

  /**
   * @param {string} bucket
   * @param {Record<string, string>} props
   */
  function applicationIdentity(bucket, props) {
    const explicit = String(props[APP_POSITION_KEY] || '').trim().toLowerCase();
    const fromLabel = String(props[LOGO_POSITION_KEY] || props[APPLICATION_KEY] || '')
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    const positionKey = explicit || fromLabel;
    return bucket && positionKey ? `${bucket}::${positionKey}` : '';
  }

  /**
   * @param {Array<{ line?: number; quantity?: number; variant_id?: number; id?: number; properties?: unknown }>} items
   * @param {string} groupId
   * @param {{ printLarge: number[]; printSmall: number[]; embroideryLarge: number[]; embroiderySmall: number[] } | null} tierConfig
   * @param {{ printLarge: number; printSmall: number; personalisation: number; personalisationAvailable: boolean } | null} trainingWearConfig
   */
  function buildFeeLineSyncPlan(items, groupId, tierConfig, trainingWearConfig) {
    const garmentQty = garmentQuantityInBulkGroup(items, groupId);
    /** @type {Array<{ line: number; quantity: number }>} */
    const qtyUpdates = [];
    /** @type {number[]} */
    const removeLines = [];
    /** @type {Array<{ id: number; quantity: number; properties: Record<string, string> }>} */
    const addItems = [];
    /** @type {Array<{ line: number; quantity: number; properties: Record<string, string> }>} */
    const propUpdates = [];

    items.forEach((it, idx) => {
      const p = normalizeProperties(it.properties || {});
      if (getLineItemBulkGroup(it) !== groupId || !isKitwearFeeLineProps(p)) {
        return;
      }
      if (garmentQty < 1 && (it.quantity || 0) !== 0) {
        removeLines.push(cartLineNumber(it, idx));
      }
    });

    if (garmentQty < 1) {
      return { qtyUpdates, removeLines, addItems, propUpdates };
    }

    const applicationMode = groupApplicationMode(items, groupId);
    const usesApplication = Boolean(applicationMode);
    const positionSummary = groupLogoPositionSummary(items, groupId);
    const embroidery = isEmbroideryGroup(items, groupId);
    const expectedEntries =
      applicationMode === 'training_wear'
        ? [
            ...expectedTrainingWearApplicationEntries(positionSummary).map((entry) => ({
              ...entry,
              quantity: garmentQty,
            })),
            ...expectedTrainingWearPersonalisationEntries(items, groupId),
          ]
        : applicationMode === 'default'
          ? expectedApplicationEntries(positionSummary, embroidery).map((entry) => ({
              ...entry,
              quantity: garmentQty,
            }))
          : [];
    /** @type {Set<string>} */
    const expectedIdentities = new Set(
      expectedEntries.map((entry) => `${entry.bucket}::${entry.positionKey}`)
    );
    const appliesTo = applicationAppliesToFromGroup(items, groupId);

    /** @type {Map<string, { line: number; it: { quantity?: number; variant_id?: number; id?: number; properties?: unknown } }>} */
    const appLinesByIdentity = new Map();

    items.forEach((it, idx) => {
      const lineNum = cartLineNumber(it, idx);
      const p = normalizeProperties(it.properties || {});
      if (getLineItemBulkGroup(it) !== groupId || !isKitwearFeeLineProps(p)) {
        return;
      }
      const feeType = feeTypeFromProps(p);
      const setupText = String(p.Setup || '').toLowerCase();
      const isApplication = feeType === 'application' || setupText.includes('application');
      if (!isApplication) {
        if (feeType === CLUB_BADGE_FEE_TYPE) {
          if ((it.quantity || 0) !== garmentQty) {
            qtyUpdates.push({ line: lineNum, quantity: garmentQty });
          }
        } else if ((it.quantity || 0) !== 1) {
          qtyUpdates.push({ line: lineNum, quantity: 1 });
        }
        return;
      }

      if (!usesApplication) {
        return;
      }

      const bucket = feeBucketFromProps(p);
      const identity = applicationIdentity(bucket, p);
      if (!identity || !expectedIdentities.has(identity)) {
        removeLines.push(lineNum);
        return;
      }
      if (appLinesByIdentity.has(identity)) {
        removeLines.push(lineNum);
        return;
      }
      appLinesByIdentity.set(identity, { line: lineNum, it });
    });

    for (const expected of expectedEntries) {
      const expectedQty = expected.quantity;
      let expectedVariantId = 0;
      if (applicationMode === 'training_wear') {
        if (trainingWearConfig) {
          const printConfigReady =
            trainingWearConfig.printLarge > 0 && trainingWearConfig.printSmall > 0;
          if (expected.bucket === 'trainingPrintLarge' && printConfigReady) {
            expectedVariantId = trainingWearConfig.printLarge;
          } else if (expected.bucket === 'trainingPrintSmall' && printConfigReady) {
            expectedVariantId = trainingWearConfig.printSmall;
          } else if (
            expected.bucket === 'trainingInitials' ||
            expected.bucket === 'trainingSmallNumber'
          ) {
            expectedVariantId = trainingWearConfig.personalisation;
          }
        }
      } else {
        const tierIdx = applicationTierIndex(garmentQty);
        const tierList = tierConfig ? tierConfig[expected.bucket] : null;
        expectedVariantId = tierList && tierIdx >= 0 && tierList[tierIdx] ? tierList[tierIdx] : 0;
      }
      const feeProps = buildApplicationFeeProperties(groupId, appliesTo, expected, applicationMode);
      const identity = `${expected.bucket}::${expected.positionKey}`;
      const existing = appLinesByIdentity.get(identity);

      if (
        (expected.bucket === 'trainingInitials' || expected.bucket === 'trainingSmallNumber') &&
        (expectedVariantId <= 0 || !trainingWearConfig || !trainingWearConfig.personalisationAvailable)
      ) {
        throw new Error('Training Wear personalisation pricing is not configured.');
      }

      if (!existing) {
        if (expectedVariantId > 0) {
          addItems.push({
            id: expectedVariantId,
            quantity: expectedQty,
            properties: feeProps,
          });
        }
        continue;
      }

      const { line, it } = existing;
      const currentVariantId = parseInt(String(it.variant_id || it.id || ''), 10);
      const currentP = normalizeProperties(it.properties || {});

      if (expectedVariantId <= 0) {
        continue;
      }

      // Default Product variants change with quantity tiers. Training Wear has one fixed
      // variant per size category, so reconciliation never swaps its established fee variant.
      if (applicationMode !== 'training_wear' && currentVariantId !== expectedVariantId) {
        removeLines.push(line);
        addItems.push({
          id: expectedVariantId,
          quantity: expectedQty,
          properties: feeProps,
        });
        continue;
      }

      const needsProp = !cartPropertiesEqual(currentP, feeProps);

      if (needsProp) {
        propUpdates.push({ line, quantity: expectedQty, properties: feeProps });
      } else if ((it.quantity || 0) !== expectedQty) {
        qtyUpdates.push({ line, quantity: expectedQty });
      }
    }

    return { qtyUpdates, removeLines, addItems, propUpdates };
  }

  /**
   * @param {Record<string, string>} props
   */
  function isEligibleNewArtworkGarmentProps(props) {
    const id = String(props[ARTWORK_ID_KEY] || '').trim().toLowerCase();
    return /^[a-f0-9]{64}$/.test(id);
  }

  /** Canonical public Uploadly property; legacy private copies remain readable for existing carts. */
  function publicArtworkFileUrl(props) {
    const entries = Object.entries(props || {})
      .filter(([key, value]) => key && key.charAt(0) !== '_' && String(value || '').trim())
      .sort(([a], [b]) => {
        const score = (key) => {
          const lower = key.toLowerCase();
          if (lower === 'image upload') return 0;
          if (lower.includes('upload') || lower.includes('image')) return 1;
          if (lower.includes('logo') || lower.includes('artwork') || lower.includes('file')) return 2;
          return 3;
        };
        return score(a) - score(b);
      });
    for (const [key, rawValue] of entries) {
      const lowerKey = key.toLowerCase();
      if (!/(upload|image|logo|artwork|file)/.test(lowerKey)) continue;
      const value = String(rawValue || '').replace(/&amp;/gi, '&').trim();
      const match = value.match(/(?:https?:)?\/\/[^\s"'<>]+/i);
      if (!match) continue;
      return match[0].startsWith('//') ? `https:${match[0]}` : match[0];
    }
    return '';
  }

  function isCentralLogoSetupProps(props) {
    if (String(props[CENTRAL_LOGO_SETUP_KEY] || '').trim().toLowerCase() === 'true') {
      return true;
    }
    return String(props.Setup || '').trim().toLowerCase() === CENTRAL_LOGO_SETUP_VALUE;
  }

  /**
   * @param {string} value
   */
  function manifestFilenameFromUrl(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';
    try {
      const absolute = raw.startsWith('//') ? `https:${raw}` : raw;
      const url = new URL(absolute, window.location.origin);
      const last = decodeURIComponent(url.pathname.split('/').filter(Boolean).pop() || '').trim();
      return last.slice(0, MANIFEST_PROPERTY_MAX_LEN);
    } catch {
      return '';
    }
  }

  /**
   * @param {Array<{ properties?: unknown; product_title?: string; title?: string; variant_title?: string }>} items
   */
  function artworkManifestFromCartItems(items) {
    const artworks = new Map();
    items.forEach((item) => {
      const props = normalizeProperties(item.properties || {});
      if (isKitwearFeeLineProps(props) || !isEligibleNewArtworkGarmentProps(props)) {
        return;
      }
      const id = String(props[ARTWORK_ID_KEY]).trim().toLowerCase();
      const fileUrl = String(
        publicArtworkFileUrl(props) || props[ARTWORK_FILE_URL_KEY] || props[ARTWORK_UPLOAD_PROP_VALUE] || ''
      ).trim();
      const filename = String(
        props[ARTWORK_FILENAME_KEY] || manifestFilenameFromUrl(fileUrl) || 'Uploaded artwork'
      )
        .trim()
        .slice(0, MANIFEST_PROPERTY_MAX_LEN);
      const product = String(item.product_title || item.title || 'Kit item').trim();
      const positions = String(
        props[LOGO_POSITION_KEY] || props['Logo positions'] || props['Logo Positions'] || 'Position not specified'
      ).trim();
      const useLabel = `${product} — ${positions}`.slice(0, MANIFEST_PROPERTY_MAX_LEN);
      const defaultApplication =
        String(props[DEFAULT_APPLICATION_KEY] || '').trim().toLowerCase() === 'true';
      if (!artworks.has(id)) {
        artworks.set(id, {
          id,
          fileUrl,
          filename,
          setupVariantId: String(props[ARTWORK_SETUP_VARIANT_KEY] || '').trim(),
          uses: new Set(),
          allDefaultApplication: defaultApplication,
        });
      } else if (!defaultApplication) {
        artworks.get(id).allDefaultApplication = false;
      }
      artworks.get(id).uses.add(useLabel);
    });
    return artworks;
  }

  /**
   * @param {Record<string, string>} props
   * @param {string} baseKey
   * @param {string[]} values
   */
  function addPackedManifestProperties(props, baseKey, values) {
    const chunks = [];
    let current = '';
    values.forEach((rawValue) => {
      const value = String(rawValue || '').trim();
      if (!value) return;
      const pieces = [];
      for (let offset = 0; offset < value.length; offset += MANIFEST_PROPERTY_MAX_LEN) {
        pieces.push(value.slice(offset, offset + MANIFEST_PROPERTY_MAX_LEN));
      }
      pieces.forEach((piece) => {
        const candidate = current ? `${current}; ${piece}` : piece;
        if (candidate.length <= MANIFEST_PROPERTY_MAX_LEN) {
          current = candidate;
          return;
        }
        if (current) chunks.push(current);
        current = piece;
      });
    });
    if (current) chunks.push(current);
    chunks.slice(0, 12).forEach((chunk, index) => {
      props[index === 0 ? baseKey : `${baseKey} (${index + 1})`] = chunk;
    });
  }

  /**
   * @param {Map<string, { id: string; fileUrl: string; filename: string; uses: Set<string> }>} artworks
   */
  function buildCentralLogoSetupProperties(artworks) {
    const allDefaultApplication =
      artworks.size > 0 &&
      Array.from(artworks.values()).every((artwork) => artwork.allDefaultApplication === true);
    const props = {
      Setup: allDefaultApplication ? 'New Logo Setup' : 'New Sponsor Logo Setup',
      [CENTRAL_LOGO_SETUP_KEY]: 'true',
    };
    let artworkNumber = 0;
    artworks.forEach((artwork) => {
      artworkNumber += 1;
      props[`Logo ${artworkNumber}`] = artwork.filename;
      if (artwork.fileUrl && artwork.fileUrl.length <= MANIFEST_PROPERTY_MAX_LEN) {
        props[`Logo ${artworkNumber} File`] = artwork.fileUrl;
      }
      addPackedManifestProperties(
        props,
        `Logo ${artworkNumber} Used On`,
        Array.from(artwork.uses)
      );
    });
    return props;
  }

  /**
   * @param {Record<string, string>} a
   * @param {Record<string, string>} b
   */
  function cartPropertiesEqual(a, b) {
    const normalize = (value) =>
      JSON.stringify(
        Object.keys(value || {})
          .sort()
          .reduce((out, key) => {
            const stringValue = String(value[key] == null ? '' : value[key]);
            if (stringValue) out[key] = stringValue;
            return out;
          }, {})
      );
    return normalize(a) === normalize(b);
  }

  /** @type {Promise<unknown | null> | null} */
  let logoSetupSyncPromise = null;

  /**
   * Cart-global reconciliation: eligible garment artwork IDs -> one setup line quantity.
   * @param {unknown} [providedCart]
   * @returns {Promise<unknown | null>}
   */
  async function syncLogoSetupFees(providedCart) {
    if (logoSetupSyncPromise) {
      return logoSetupSyncPromise;
    }
    logoSetupSyncPromise = (async () => {
      const cartUrl = `${window.themeVariables.routes.cartUrl}.js`;
      const changeUrl = `${window.themeVariables.routes.cartChangeUrl}.js`;
      const addUrl = `${window.themeVariables.routes.cartAddUrl}.js`;
      let cart = providedCart && Array.isArray(providedCart.items) ? providedCart : null;
      if (!cart) {
        const response = await nativeFetch(cartUrl, { credentials: 'same-origin', cache: 'no-store' });
        if (!response.ok) throw new Error('Could not read the cart for logo setup reconciliation.');
        cart = await response.json();
      }

      let items = Array.isArray(cart.items) ? cart.items : [];
      const artworks = artworkManifestFromCartItems(items);
      const expectedQuantity = artworks.size;
      const centralLines = [];
      items.forEach((item, index) => {
        const props = normalizeProperties(item.properties || {});
        if (isCentralLogoSetupProps(props)) {
          centralLines.push({ item, line: cartLineNumber(item, index), props });
        }
      });

      let changed = false;
      let lastPayload = null;
      const postChange = async (body) => {
        const response = await nativeFetch(changeUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ ...body, sections: ['mini-cart'] }),
        });
        if (!response.ok) throw new Error('Could not update the logo setup fee.');
        lastPayload = await response.json();
        changed = true;
      };

      if (expectedQuantity < 1) {
        const lines = centralLines.map((row) => row.line).sort((a, b) => b - a);
        for (const line of lines) {
          await postChange({ line, quantity: 0 });
        }
      } else {
        let expectedVariantId = 0;
        for (const artwork of artworks.values()) {
          const parsed = parseInt(String(artwork.setupVariantId || ''), 10);
          if (!Number.isNaN(parsed) && parsed > 0) {
            expectedVariantId = parsed;
            break;
          }
        }
        if (!expectedVariantId && centralLines.length) {
          expectedVariantId = parseInt(
            String(centralLines[0].item.variant_id || centralLines[0].item.id || ''),
            10
          );
        }
        if (!expectedVariantId) {
          throw new Error('The £15 logo setup variant could not be resolved from eligible garment lines.');
        }

        const expectedProps = buildCentralLogoSetupProperties(artworks);
        let keeper = centralLines.find((row) => {
          const id = parseInt(String(row.item.variant_id || row.item.id || ''), 10);
          return id === expectedVariantId;
        });
        const removeRows = centralLines.filter((row) => row !== keeper);
        if (!keeper && centralLines.length) {
          removeRows.splice(0, removeRows.length, ...centralLines);
        }
        for (const row of removeRows.sort((a, b) => b.line - a.line)) {
          await postChange({ line: row.line, quantity: 0 });
        }

        if (removeRows.length) {
          const freshResponse = await nativeFetch(cartUrl, { credentials: 'same-origin', cache: 'no-store' });
          cart = await freshResponse.json();
          items = Array.isArray(cart.items) ? cart.items : [];
          keeper = null;
          items.forEach((item, index) => {
            const props = normalizeProperties(item.properties || {});
            const id = parseInt(String(item.variant_id || item.id || ''), 10);
            if (
              !keeper &&
              id === expectedVariantId &&
              isCentralLogoSetupProps(props)
            ) {
              keeper = { item, line: cartLineNumber(item, index), props };
            }
          });
        }

        if (!keeper) {
          const response = await nativeFetch(addUrl, {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              items: [{ id: expectedVariantId, quantity: expectedQuantity, properties: expectedProps }],
              sections: ['mini-cart'],
            }),
          });
          if (!response.ok) throw new Error('Could not add the reconciled logo setup fee.');
          lastPayload = await response.json();
          changed = true;
        } else if (
          Number(keeper.item.quantity || 0) !== expectedQuantity ||
          !cartPropertiesEqual(keeper.props, expectedProps)
        ) {
          const itemKey = String(keeper.item.key || '').trim();
          await postChange({
            ...(itemKey ? { id: itemKey } : { line: keeper.line }),
            quantity: expectedQuantity,
            properties: expectedProps,
          });
        }
      }

      if (!changed) {
        return null;
      }
      const finalResponse = await nativeFetch(cartUrl, { credentials: 'same-origin', cache: 'no-store' });
      const finalCart = await finalResponse.json();
      if (lastPayload && lastPayload.sections) {
        finalCart.sections = lastPayload.sections;
      }
      return finalCart;
    })();
    try {
      return await logoSetupSyncPromise;
    } finally {
      logoSetupSyncPromise = null;
    }
  }

  /**
   * @param {unknown} cart
   * @param {number} [depth]
   * @returns {Promise<unknown | null>} cart payload from last change (may include sections), or null
   */
  async function syncFeeLinesInCart(cart, depth = 0) {
    const logoSetupPayload = await syncLogoSetupFees(cart);
    const workingCart = logoSetupPayload || cart;
    const items = /** @type {Array<{ line?: number; quantity?: number; variant_id?: number; id?: number; properties?: unknown }>} */ (
      workingCart.items || []
    );
    /** @type {Set<string>} */
    const groups = new Set();
    items.forEach((it) => {
      const g = getLineItemBulkGroup(it);
      if (g) {
        groups.add(g);
      }
    });
    if (!groups.size) {
      return logoSetupPayload;
    }
    const tierConfig = readAppTierConfig();
    const trainingWearConfig = readTrainingWearAppConfig();
    /** @type {Array<{ line: number; quantity: number }>} */
    const allQtyUpdates = [];
    /** @type {number[]} */
    const allRemoveLines = [];
    /** @type {Array<{ id: number; quantity: number; properties: Record<string, string> }>} */
    const allAddItems = [];
    /** @type {Array<{ line: number; quantity: number; properties: Record<string, string> }>} */
    const allPropUpdates = [];

    for (const groupId of groups) {
      const plan = buildFeeLineSyncPlan(items, groupId, tierConfig, trainingWearConfig);
      allQtyUpdates.push(...plan.qtyUpdates);
      allRemoveLines.push(...plan.removeLines);
      allAddItems.push(...plan.addItems);
      allPropUpdates.push(...plan.propUpdates);
    }

    const removeLines = [...new Set(allRemoveLines)].sort((a, b) => b - a);
    if (
      !removeLines.length &&
      !allAddItems.length &&
      !allQtyUpdates.length &&
      !allPropUpdates.length
    ) {
      return logoSetupPayload;
    }

    const changeUrl = `${window.themeVariables.routes.cartChangeUrl}.js`;
    const addUrl = `${window.themeVariables.routes.cartAddUrl}.js`;
    let lastPayload = null;
    const needsSections = true;

    for (let i = 0; i < removeLines.length; i += 1) {
      const isLastRemove =
        i === removeLines.length - 1 && !allAddItems.length && !allQtyUpdates.length;
      const response = await nativeFetch(changeUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: removeLines[i],
          quantity: 0,
          ...(isLastRemove && needsSections ? { sections: ['mini-cart'] } : {}),
        }),
      });
      if (isLastRemove && response.ok) {
        try {
          lastPayload = await response.json();
        } catch {
          lastPayload = null;
        }
      }
    }

    if (allAddItems.length) {
      const response = await nativeFetch(addUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: allAddItems,
          ...(needsSections ? { sections: ['mini-cart'] } : {}),
        }),
      });
      if (response.ok) {
        try {
          lastPayload = await response.json();
        } catch {
          lastPayload = null;
        }
      }
    }

    if (allPropUpdates.length) {
      for (let i = 0; i < allPropUpdates.length; i += 1) {
        const row = allPropUpdates[i];
        const isLast = i === allPropUpdates.length - 1 && !allQtyUpdates.length;
        const response = await nativeFetch(changeUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            line: row.line,
            quantity: row.quantity,
            properties: row.properties,
            ...(isLast && needsSections ? { sections: ['mini-cart'] } : {}),
          }),
        });
        if (isLast && response.ok) {
          try {
            lastPayload = await response.json();
          } catch {
            lastPayload = null;
          }
        }
      }
    }

    if (allQtyUpdates.length) {
      for (let i = 0; i < allQtyUpdates.length; i += 1) {
        const row = allQtyUpdates[i];
        const isLast = i === allQtyUpdates.length - 1;
        const response = await nativeFetch(changeUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            line: row.line,
            quantity: row.quantity,
            ...(isLast && needsSections ? { sections: ['mini-cart'] } : {}),
          }),
        });
        if (isLast && response.ok) {
          try {
            lastPayload = await response.json();
          } catch {
            lastPayload = null;
          }
        }
      }
    }

    if (
      (removeLines.length || allAddItems.length) &&
      (allQtyUpdates.length || allPropUpdates.length) &&
      depth < 2
    ) {
      const freshRes = await nativeFetch(`${window.themeVariables.routes.cartUrl}.js`, {
        credentials: 'same-origin',
      });
      const freshCart = await freshRes.json();
      const followUp = await syncFeeLinesInCart(freshCart, depth + 1);
      if (followUp) {
        return followUp;
      }
    }

    return lastPayload || logoSetupPayload;
  }

  /**
   * After kit lines are removed, the fee line can remain if bulk removal did not run.
   * Remove fee-only lines: bulk group present, line looks like setup fee, no non-fee line shares that group.
   */
  let orphanFeeCleanupRunning = false;

  /**
   * @returns {Promise<unknown | null>} cart payload if lines were removed, else null
   */
  async function removeOrphanLogoFeeLines() {
    if (orphanFeeCleanupRunning) {
      return null;
    }
    orphanFeeCleanupRunning = true;
    try {
      const cartRes = await nativeFetch(`${window.themeVariables.routes.cartUrl}.js`, {
        credentials: 'same-origin',
      });
      const cart = await cartRes.json();
      const items = /** @type {Array<{ line?: number; properties?: unknown }>} */ (cart.items || []);
      /** @type {Set<string>} */
      const groupsWithKit = new Set();
      items.forEach((it) => {
        const p = normalizeProperties(it.properties || {});
        const g = getLineItemBulkGroup(it);
        if (!g) {
          return;
        }
        if (!isKitwearFeeLineProps(p)) {
          groupsWithKit.add(g);
        }
      });
      const changeUrl = `${window.themeVariables.routes.cartChangeUrl}.js`;
      /** @type {number[]} */
      const toRemove = [];
      items.forEach((it, idx) => {
        const p = normalizeProperties(it.properties || {});
        const g = getLineItemBulkGroup(it);
        if (!g) {
          return;
        }
        if (!isKitwearFeeLineProps(p)) {
          return;
        }
        if (!groupsWithKit.has(g)) {
          toRemove.push(cartLineNumber(it, idx));
        }
      });
      if (!toRemove.length) {
        return null;
      }
      toRemove.sort((a, b) => b - a);
      let lastPayload = null;
      for (let i = 0; i < toRemove.length; i += 1) {
        const lineNum = toRemove[i];
        const isLast = i === toRemove.length - 1;
        const response = await nativeFetch(changeUrl, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            line: lineNum,
            quantity: 0,
            ...(isLast ? { sections: ['mini-cart'] } : {}),
          }),
        });
        if (isLast && response.ok) {
          try {
            lastPayload = await response.json();
          } catch {
            lastPayload = null;
          }
        }
      }
      if (!lastPayload) {
        const cart2Res = await nativeFetch(`${window.themeVariables.routes.cartUrl}.js`, {
          credentials: 'same-origin',
        });
        lastPayload = await cart2Res.json();
      }
      return lastPayload;
    } catch {
      return null;
    } finally {
      orphanFeeCleanupRunning = false;
    }
  }

  function scheduleOrphanLogoFeeCleanup() {
    scheduleCartMaintenance();
  }

  function scheduleOrphanLogoFeeCleanupAndQtySync() {
    scheduleCartMaintenance();
  }

  function scheduleCartMaintenance() {
    if (isCartMaintenanceSuppressed()) {
      return;
    }
    window.clearTimeout(cartMaintenanceTimer);
    cartMaintenanceTimer = window.setTimeout(() => {
      runCartMaintenance();
    }, 180);
  }

  async function runCartMaintenance() {
    if (cartMaintenanceRunning || isCartMaintenanceSuppressed()) {
      return;
    }
    cartMaintenanceRunning = true;
    try {
      let refreshPayload = await removeOrphanLogoFeeLines();
      if (isCartMaintenanceSuppressed()) {
        return;
      }
      bulkQtySyncInProgress = true;
      try {
        const cartRes = await nativeFetch(`${window.themeVariables.routes.cartUrl}.js`, {
          credentials: 'same-origin',
        });
        const cart = await cartRes.json();
        const syncPayload = await syncFeeLinesInCart(cart);
        if (syncPayload) {
          refreshPayload = syncPayload;
        }
      } finally {
        bulkQtySyncInProgress = false;
      }
      if (refreshPayload) {
        suppressCartMaintenance(900);
        dispatchCartUiRefresh(refreshPayload, 600);
      }
    } catch {
      // ignore
    } finally {
      cartMaintenanceRunning = false;
    }
  }

  /** @type {number | undefined} */
  let bulkGroupFeeQtySyncTimer;

  function scheduleBulkGroupFeeQtySync() {
    scheduleCartMaintenance();
  }

  document.documentElement.addEventListener('cart:updated', scheduleCartMaintenance);
  document.addEventListener('line-item-quantity:change:end', scheduleCartMaintenance);

  /**
   * @param {unknown} line
   * @returns {string}
   */
  function getLineItemBulkGroup(line) {
    if (!line || typeof line !== 'object') {
      return '';
    }
    const p = /** @type {{ properties?: unknown }} */ (line).properties;
    if (!p) {
      return '';
    }
    const normalized = normalizeProperties(p);
    const g =
      normalized[BULK_GROUP_KEY] ||
      normalized[PUBLIC_GROUP_KEY] ||
      '';
    return g != null ? String(g).trim() : '';
  }

  /**
   * @param {string} href
   */
  function isCartChangeLink(href) {
    try {
      const u = new URL(href, window.location.origin);
      const p = u.pathname;
      if (p === '/cart/change' || p.endsWith('/cart/change')) {
        return true;
      }
      const expected = new URL(window.themeVariables.routes.cartChangeUrl, window.location.origin).pathname;
      return p === expected;
    } catch {
      return false;
    }
  }

  /**
   * @param {string} href
   * @returns {{ line: string | null; quantity: number | null }}
   */
  function parseCartChangeParams(href) {
    try {
      const u = new URL(href, window.location.origin);
      const line = u.searchParams.get('line');
      const q = u.searchParams.get('quantity');
      const quantity = q != null ? parseInt(q, 10) : NaN;
      return { line, quantity: Number.isNaN(quantity) ? null : quantity };
    } catch {
      return { line: null, quantity: null };
    }
  }

  /**
   * @param {string} href
   */
  function isLineRemovalToZero(href) {
    const { line, quantity } = parseCartChangeParams(href);
    return Boolean(line) && quantity === 0;
  }

  /**
   * @param {string} groupId
   */
  /**
   * @param {string} groupId
   * @returns {Promise<unknown | null>}
   */
  async function removeAllLinesInBulkGroup(groupId) {
    const cartUrl = `${window.themeVariables.routes.cartUrl}.js`;
    const changeUrl = `${window.themeVariables.routes.cartChangeUrl}.js`;

    const cartRes = await nativeFetch(cartUrl, { credentials: 'same-origin' });
    const cart = await cartRes.json();
    const items = /** @type {Array<{ line?: number; properties?: unknown }>} */ (cart.items || []);
    /** @type {number[]} */
    const lineNums = [];
    items.forEach((it, idx) => {
      if (getLineItemBulkGroup(it) === groupId) {
        lineNums.push(cartLineNumber(it, idx));
      }
    });
    if (!lineNums.length) {
      return null;
    }
    lineNums.sort((a, b) => b - a);
    let lastPayload = null;
    for (let i = 0; i < lineNums.length; i += 1) {
      const lineNum = lineNums[i];
      const isLast = i === lineNums.length - 1;
      const response = await nativeFetch(changeUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line: lineNum,
          quantity: 0,
          ...(isLast ? { sections: ['mini-cart'] } : {}),
        }),
      });
      if (isLast && response.ok) {
        try {
          lastPayload = await response.json();
        } catch {
          lastPayload = null;
        }
      }
    }
    if (!lastPayload) {
      const cart2Res = await nativeFetch(cartUrl, { credentials: 'same-origin' });
      lastPayload = await cart2Res.json();
    }
    return lastPayload;
  }

  /**
   * Remove garment line → entire bulk group (kit + all fees). Fee line → that line only (default cart change).
   * @param {string} lineStr 1-based line index from cart UI
   * @returns {Promise<unknown | null>} cart payload if bulk group was removed, else null
   */
  async function tryRemoveFullBulkGroup(lineStr) {
    const cartRes = await nativeFetch(`${window.themeVariables.routes.cartUrl}.js`, {
      credentials: 'same-origin',
    });
    const cart = await cartRes.json();
    const items = /** @type {Array<{ line?: number; properties?: unknown }>} */ (cart.items || []);
    const target = findCartItemByLine(items, lineStr);
    if (!target) {
      return null;
    }
    const groupId = getLineItemBulkGroup(target);
    if (!groupId) {
      return null;
    }
    const targetProps = normalizeProperties(target.properties || {});
    if (isKitwearFeeLineProps(targetProps)) {
      return null;
    }
    const garmentLines = items.filter((item) => {
      const props = normalizeProperties(item.properties || {});
      return getLineItemBulkGroup(item) === groupId && !isKitwearFeeLineProps(props);
    });
    const groupedApplication = garmentLines.some((item) => {
      const props = normalizeProperties(item.properties || {});
      return (
        String(props[DEFAULT_APPLICATION_KEY] || '').trim().toLowerCase() === 'true' ||
        String(props[TRAINING_WEAR_APPLICATION_KEY] || '').trim().toLowerCase() === 'true'
      );
    });
    // Application products use one garment line per selected size. Removing one size must
    // leave the remaining sizes and let reconciliation update every fee-line quantity.
    if (groupedApplication && garmentLines.length > 1) {
      return null;
    }
    return removeAllLinesInBulkGroup(groupId);
  }

  /**
   * Club badge surcharge line removed manually:
   * remove that fee line, then reset grouped kit lines back to printed badge label.
   * @param {string} lineStr
   * @returns {Promise<unknown | null>}
   */
  async function tryRemoveClubBadgeFeeAndResetGroup(lineStr) {
    const cartRes = await nativeFetch(`${window.themeVariables.routes.cartUrl}.js`, {
      credentials: 'same-origin',
    });
    const cart = await cartRes.json();
    const items = /** @type {Array<{ line?: number; quantity?: number; properties?: unknown }>} */ (
      cart.items || []
    );
    const target = findCartItemByLine(items, lineStr);
    if (!target) {
      return null;
    }
    const targetProps = normalizeProperties(target.properties || {});
    if (feeTypeFromProps(targetProps) !== CLUB_BADGE_FEE_TYPE) {
      return null;
    }
    const groupId = getLineItemBulkGroup(target);
    if (!groupId) {
      return null;
    }
    const targetLineNum = parseInt(String(target.line || lineStr), 10);
    if (Number.isNaN(targetLineNum) || targetLineNum < 1) {
      return null;
    }
    const changeUrl = `${window.themeVariables.routes.cartChangeUrl}.js`;
    const printedBadgeLabel = 'Printed club badge (included)';

    // Remove the surcharge line first.
    await nativeFetch(changeUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        line: targetLineNum,
        quantity: 0,
      }),
    });

    // Refresh cart and update grouped kit lines.
    const cart2Res = await nativeFetch(`${window.themeVariables.routes.cartUrl}.js`, {
      credentials: 'same-origin',
    });
    const cart2 = await cart2Res.json();
    const items2 = /** @type {Array<{ line?: number; quantity?: number; properties?: unknown }>} */ (
      cart2.items || []
    );
    const groupKits = [];
    items2.forEach((it, idx) => {
      const props = normalizeProperties(it.properties || {});
      if (getLineItemBulkGroup(it) !== groupId || isKitwearFeeLineProps(props)) {
        return;
      }
      const lineNum = cartLineNumber(it, idx);
      const itemKey = String(/** @type {{ key?: unknown }} */ (it).key || '').trim();
      groupKits.push({
        line: lineNum,
        key: itemKey,
        quantity: it.quantity || 1,
        properties: props,
      });
    });
    for (const row of groupKits) {
      const nextProps = { ...row.properties };
      nextProps['Club badge'] = printedBadgeLabel;
      await nativeFetch(changeUrl, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...(row.key ? { id: row.key } : { line: row.line }),
          quantity: row.quantity,
          properties: nextProps,
        }),
      });
    }

    // Final refreshed payload for UI update.
    const cart3Res = await nativeFetch(`${window.themeVariables.routes.cartUrl}.js`, {
      credentials: 'same-origin',
    });
    return cart3Res.json();
  }

  /**
   * @param {string} href
   */
  async function defaultCartChangeFromLink(href) {
    try {
      const u = new URL(href, window.location.origin);
      const line = u.searchParams.get('line');
      const id = u.searchParams.get('id');
      const quantity = parseInt(u.searchParams.get('quantity') || '0', 10);
      const response = await nativeFetch(`${window.themeVariables.routes.cartChangeUrl}.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          line,
          id,
          quantity,
          sections: ['mini-cart'],
        }),
      });
      if (!response.ok) {
        return;
      }
      if (window.themeVariables.settings.pageType === 'cart') {
        window.location.reload();
        return;
      }
      const cartContent = await response.json();
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:updated', {
          bubbles: true,
          detail: { cart: cartContent },
        })
      );
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: {
            cart: cartContent,
            replacementDelay: quantity === 0 ? 600 : 750,
          },
        })
      );
    } catch {
      // ignore
    }
  }

  /**
   * @param {string} lineStr
   */
  /**
   * @param {unknown} [cartPayload]
   */
  async function refreshCartAfterBulkRemoval(cartPayload) {
    let cart = cartPayload || null;
    if (!cart) {
      const cartRes = await nativeFetch(`${window.themeVariables.routes.cartUrl}.js`, {
        credentials: 'same-origin',
      });
      cart = await cartRes.json();
    }
    const logoSetupPayload = await syncLogoSetupFees(cart);
    if (logoSetupPayload) {
      cart = logoSetupPayload;
    }
    suppressCartMaintenance(900);
    dispatchCartUiRefresh(cart, 600);
  }

  document.addEventListener(
    'click',
    (e) => {
      const a = /** @type {HTMLElement | null} */ (e.target).closest('a');
      if (!a || !a.href) {
        return;
      }
      if (!isCartChangeLink(a.href) || !isLineRemovalToZero(a.href)) {
        return;
      }
      const { line } = parseCartChangeParams(a.href);
      if (!line) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      (async () => {
        const payload = await tryRemoveFullBulkGroup(line);
        if (payload) {
          await refreshCartAfterBulkRemoval(payload);
          return;
        }
        const clubBadgePayload = await tryRemoveClubBadgeFeeAndResetGroup(line);
        if (clubBadgePayload) {
          await refreshCartAfterBulkRemoval(clubBadgePayload);
          return;
        }
        await defaultCartChangeFromLink(a.href);
      })();
    },
    true
  );

  document.addEventListener(
    'change',
    (e) => {
      const t = e.target;
      if (!(t instanceof HTMLInputElement) || !t.hasAttribute('data-line')) {
        return;
      }
      const q = parseInt(t.value, 10);
      if (q !== 0) {
        return;
      }
      const line = t.getAttribute('data-line');
      if (!line) {
        return;
      }

      e.preventDefault();
      e.stopPropagation();

      (async () => {
        const payload = await tryRemoveFullBulkGroup(line);
        if (payload) {
          await refreshCartAfterBulkRemoval(payload);
          return;
        }
        const clubBadgePayload = await tryRemoveClubBadgeFeeAndResetGroup(line);
        if (clubBadgePayload) {
          await refreshCartAfterBulkRemoval(clubBadgePayload);
          return;
        }
        const href = `${window.themeVariables.routes.cartChangeUrl}?quantity=0&line=${encodeURIComponent(line)}`;
        await defaultCartChangeFromLink(href);
      })();
    },
    true
  );

  /**
   * `LineItemQuantity` (theme.js) POSTs `/cart/change.js` directly — no link click — so bulk group removal
   * must run here too, using native fetch internally to avoid recursion.
   */
  window.fetch = async function fetchWithKitwearBulkGroup(input, init) {
    const url =
      typeof input === 'string'
        ? input
        : input instanceof Request
          ? input.url
          : '';
    const method = (init && init.method) || (input instanceof Request ? input.method : 'GET');
    if (
      typeof url === 'string' &&
      /\/cart\/change\.js(\?|$)/.test(url) &&
      method === 'POST' &&
      init &&
      typeof init.body === 'string' &&
      !bulkQtySyncInProgress
    ) {
      try {
        const data = JSON.parse(init.body);
        const line = data.line != null ? String(data.line) : '';
        if (line && data.quantity === 0) {
          const bulkPayload = await tryRemoveFullBulkGroup(line);
          if (bulkPayload) {
            const logoSetupPayload = await syncLogoSetupFees(bulkPayload);
            const finalBulkPayload = logoSetupPayload || bulkPayload;
            suppressCartMaintenance(900);
            return new Response(JSON.stringify(finalBulkPayload), {
              status: 200,
              headers: { 'Content-Type': 'application/json' },
            });
          }
          const clubBadgePayload = await tryRemoveClubBadgeFeeAndResetGroup(line);
          if (clubBadgePayload) {
            suppressCartMaintenance(900);
            return new Response(JSON.stringify(clubBadgePayload), {
              status: 200,
              headers: { 'Content-Type': 'application/json' },
            });
          }

          const cartBeforeRes = await nativeFetch(`${window.themeVariables.routes.cartUrl}.js`, {
            credentials: 'same-origin',
          });
          const cartBefore = await cartBeforeRes.json();
          const itemsBefore = cartBefore.items || [];
          const targetBefore = findCartItemByLine(itemsBefore, line);
          const groupBefore = targetBefore ? getLineItemBulkGroup(targetBefore) : '';
          if (groupBefore && groupUsesLogoApplication(itemsBefore, groupBefore)) {
            const response = await nativeFetch(input, init);
            if (!response.ok) {
              return response;
            }
            bulkQtySyncInProgress = true;
            try {
              const cartAfter = await response.clone().json();
              const synced = await syncFeeLinesInCart(cartAfter);
              const cartFinal =
                synced ||
                (await (
                  await nativeFetch(`${window.themeVariables.routes.cartUrl}.js`, {
                    credentials: 'same-origin',
                  })
                ).json());
              return new Response(JSON.stringify(cartFinal), {
                status: response.status,
                headers: { 'Content-Type': 'application/json' },
              });
            } catch {
              return response;
            } finally {
              bulkQtySyncInProgress = false;
            }
          }
        }

        if (line && data.quantity != null && data.quantity !== 0) {
          const cartRes = await nativeFetch(`${window.themeVariables.routes.cartUrl}.js`, {
            credentials: 'same-origin',
          });
          const cartBefore = await cartRes.json();
          const itemsBefore = /** @type {Array<{ line?: number; properties?: unknown }>} */ (
            cartBefore.items || []
          );
          const target = findCartItemByLine(itemsBefore, line);
          const groupId = target ? getLineItemBulkGroup(target) : '';
          const targetProps = target ? normalizeProperties(target.properties || {}) : {};
          const isFeeLine = isKitwearFeeLineProps(targetProps);

          if (groupId && isFeeLine) {
            const garmentQty = garmentQuantityInBulkGroup(itemsBefore, groupId);
            const feeType = feeTypeFromProps(targetProps);
            const setupText = String(targetProps.Setup || '').toLowerCase();
            const isApplication =
              feeType === 'application' || setupText.includes('application');
            if (isApplication) {
              data.quantity = garmentQty;
            } else if (feeType === CLUB_BADGE_FEE_TYPE) {
              data.quantity = garmentQty;
            } else {
              data.quantity = 1;
            }
            init = {
              ...init,
              body: JSON.stringify(data),
            };
          }

          const response = await nativeFetch(input, init);

          if (groupId && !isFeeLine) {
            bulkQtySyncInProgress = true;
            try {
              const cartAfter = await response.clone().json();
              const synced = await syncFeeLinesInCart(cartAfter);
              const cartFinal =
                synced ||
                (await (
                  await nativeFetch(`${window.themeVariables.routes.cartUrl}.js`, {
                    credentials: 'same-origin',
                  })
                ).json());
              return new Response(JSON.stringify(cartFinal), {
                status: response.status,
                headers: { 'Content-Type': 'application/json' },
              });
            } catch {
              return response;
            } finally {
              bulkQtySyncInProgress = false;
            }
          }

          return response;
        }
      } catch {
        // fall through
      }
    }
    return nativeFetch(input, init);
  };

  /**
   * Visual grouping for bulk kit + fee lines (cart page table + mini-cart drawer).
   */
  function moveCentralLogoSetupLineToBottom() {
    document.querySelectorAll('.line-item-table__list').forEach((tbody) => {
      const centralRows = Array.from(
        tbody.querySelectorAll(':scope > [data-kitwear-logo-setup-central="true"]')
      );
      centralRows.forEach((row) => tbody.appendChild(row));
    });

    const drawerForm = document.querySelector('#mini-cart-form');
    if (drawerForm) {
      const lineItems = Array.from(drawerForm.querySelectorAll(':scope > line-item'));
      const centralItems = lineItems.filter(
        (item) => item.getAttribute('data-kitwear-logo-setup-central') === 'true'
      );
      const lastOtherItem = lineItems.filter((item) => !centralItems.includes(item)).pop();
      if (lastOtherItem) {
        lastOtherItem.after(...centralItems);
      }
    }
  }

  function applyCartLineGrouping() {
    moveCentralLogoSetupLineToBottom();
    const groupRows = /** @type {HTMLElement[]} */ (
      Array.from(document.querySelectorAll('[data-kitwear-bulk-group]')).filter((el) => {
        const g = (el.getAttribute('data-kitwear-bulk-group') || '').trim();
        return Boolean(g);
      })
    );
    groupRows.forEach((el) => {
      el.classList.remove(
        'line-item--kitwear-group-first',
        'line-item--kitwear-group-last',
        'line-item--kitwear-group-middle'
      );
    });

    /** @type {Map<string, HTMLElement[]>} */
    const byGroup = new Map();
    groupRows.forEach((el) => {
      const g = (el.getAttribute('data-kitwear-bulk-group') || '').trim();
      if (!byGroup.has(g)) {
        byGroup.set(g, []);
      }
      byGroup.get(g).push(el);
    });

    byGroup.forEach((rows) => {
      const visibleRows = rows.filter(
        (el) => !el.classList.contains('line-item--kitwear-fee--collapsed')
      );
      if (visibleRows.length < 2) {
        return;
      }
      visibleRows[0].classList.add('line-item--kitwear-group-first');
      visibleRows[visibleRows.length - 1].classList.add('line-item--kitwear-group-last');
      for (let i = 1; i < visibleRows.length - 1; i += 1) {
        visibleRows[i].classList.add('line-item--kitwear-group-middle');
      }
    });
  }

  function scheduleKitwearCartCountOverride(event) {
    const cart = event && event.detail && event.detail.cart;
    if (cart) {
      queueMicrotask(() => updateKitwearCartCountDisplays(cart));
      return;
    }
    // Theme may fetch `/cart.js` asynchronously and set `item_count` on `cart-count`.
    window.setTimeout(() => {
      nativeFetch(`${window.themeVariables.routes.cartUrl}.js`, { cache: 'no-store' })
        .then((response) => response.json())
        .then((freshCart) => updateKitwearCartCountDisplays(freshCart))
        .catch(() => {});
    }, 120);
  }

  document.documentElement.addEventListener('cart:updated', scheduleKitwearCartCountOverride);
  document.documentElement.addEventListener('cart:refresh', scheduleKitwearCartCountOverride);

  window.KitwearCartBulk = {
    applyCartLineGrouping,
    syncFeeLinesInCart,
    syncLogoSetupFees,
    kitwearGarmentCartCountFromCart,
    updateKitwearCartCountDisplays,
    suppressCartMaintenance,
  };

  function initCartKitwearUi() {
    applyCartLineGrouping();
    // Avoid burst /cart/change.js calls on every page (can 502 the theme dev proxy).
    if (window.themeVariables.settings.pageType === 'cart') {
      scheduleCartMaintenance();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartKitwearUi);
  } else {
    initCartKitwearUi();
  }

  document.documentElement.addEventListener('cart:refresh', () => {
    window.setTimeout(applyCartLineGrouping, 50);
  });
})();
